import { useEffect, useState } from "react";

export type ColorTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "abc-docs-theme";

export function getInitialColorTheme(): ColorTheme {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
    ? "dark"
    : "light";
}

/** Document only, so a forced theme never overwrites the stored preference. */
export function setDocumentColorTheme(theme: ColorTheme): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

export function applyColorTheme(theme: ColorTheme): void {
  setDocumentColorTheme(theme);
  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
}

interface UseColorThemeOptions {
  /**
   * Set to false on surfaces with no dark styling (the legacy experience).
   * The document renders light and the stored preference is left untouched, so
   * it comes back once the surface supports dark again.
   */
  darkModeEnabled?: boolean;
}

export function useColorTheme({
  darkModeEnabled = true,
}: UseColorThemeOptions = {}) {
  const [preference, setTheme] = useState<ColorTheme>(getInitialColorTheme);
  const theme: ColorTheme = darkModeEnabled ? preference : "light";

  useEffect(() => {
    if (darkModeEnabled) {
      applyColorTheme(theme);
      return;
    }
    setDocumentColorTheme("light");
  }, [darkModeEnabled, theme]);

  return { theme, setTheme, darkModeEnabled };
}
